import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
//import emailjs from "@emailjs/browser";
import { Contact } from "lucide-react";

export const FAQPage = () => {
  const navigate = useNavigate();
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const BASEURL = process.env.REACT_APP_BASEURL;

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    console.log("Demo request:", formData);
    try {
      const res = await fetch(`${BASEURL}/api/WebsiteEmailApi/SendContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Name:formData.name,
          Phone:formData.phone,
          Email:formData.email,
          Company:formData.company,
          Message:formData.message
        })
      });
      if (res.status) {
        toast.success("Demo request submitted successfully! We'll contact you soon.");
        setDemoDialogOpen(false);
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      }
    } catch (error) {
      console.log(error.stack);
      toast.warning("Error booking a demo. Please try again");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center">
              <a href="/" data-testid="logo">
                <img
                  src="https://customer-assets.emergentagent.com/job_fintech-demo-9/artifacts/hrowc68u_image.png"
                  alt="TurboPay"
                  className="h-8 md:h-10 w-auto"
                />
              </a>
            </div>
            <div className="hidden lg:flex items-center space-x-8 text-lg">
              <a href="/" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors">Home</a>
              {/* <a href="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors">Pricing</a> */}
              <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              {/* <a href="/faq" className="text-[#1F497D] hover:text-[#183a64] font-medium transition-colors">FAQs</a> */}
              <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-faq">
                FAQs
              </Link>
            </div>
            <div className="hidden lg:block">
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-11 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95">
                    Get Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Book a Demo</DialogTitle>
                    <DialogDescription>
                      Fill out the form below and we'll get back to you shortly.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" name="name" required value={formData.name} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" name="company" value={formData.company} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea id="message" name="message" rows={3} value={formData.message} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold">
                      Book Demo
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </nav>

      {/* FAQ Section */}
      <section className="py-12  md:py-8" data-testid="faq-section">
        <div className="container-custom">
          <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-center mb-4 text-slate-900">
            Frequently Asked Questions
          </h1>
          <p className="text-center text-lg text-slate-600 mb-12 max-w-2xl mx-auto">
            Find answers to common questions about TurboPay's collections software and funding services
          </p>

          <div className="max-w-4xl mx-auto space-y-12 text-base md:text-lg">
            {/* General FAQs */}
            <div>
              <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-[#1F497D]">General</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="general-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do You Offer Free Trial?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base">
                    Yes, TurboPay's free trial lets you explore its powerful features for 15 days at no cost. Currently, the trial is available to Tally ERP users and those willing to upload receivables data via Excel. Please email us your details on sales@TurboPay.in
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Which ERP and Accounting Softwares do you support?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    At present, TurboPay is integrated with Tally.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do you have a smartphone app?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, TurboPay's smart mobile app empowers sales teams to collect payments faster and more efficiently.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Is my data secure?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    We adopt industry appropriate data collection, storage and processing practices, and security measures to protect users' personal Information. These security measures include password protected directories and databases, encryption of key fields in the database, SSL (Secure Sockets Layered) technology, and IP based access restrictions. Our servers are hosted on AWS servers and backed up daily.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="general-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How do I know if TurboPay is right for my business?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is right for all business that sell on credit. Please refer our Benefits section to see how TurboPay can help your business.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Collections FAQs */}
            {/* <div>
              <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-200">Collections</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="collections-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Does TurboPay support multi-currency?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, if your ERP or accounting software supports multi-currency, the same gets reflected in TurboPay.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    What is the "from" address of emails sent out from TurboPay to my customers?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    You can decide from which email ID you want to send emails to your customers. You can configure TurboPay to send emails from your Gmail, or your business email ID.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Can I login from web, smart phone, and smart tab with one user license?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. A user can login to TurboPay from web, smart phone or tab with one user license.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How will I learn to use the software?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is very user friendly. We offer product training at the time of implementation. Further, we also provide on-demand support through phone, email and audio / video conference calls. For any specific support requirements, please email us on support@TurboPay.com.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How long is the implementation process?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    TurboPay is quick and easy to implement—just a few hours for Tally ERP users or businesses uploading receivables via Excel. We provide instant training so your team can start using it right away. Most customers fully adopt TurboPay within days and see measurable benefits in just one week.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-6" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Does TurboPay have customer support?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, we provide support via telephone, email and audio / video conference calls to all our customers.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="collections-7" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do you writeback any data into our ERP or accounting software?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    No. We only read data and do not write back any data into your ERP or accounting software.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div> */}

            {/* Distributor Funding FAQs */}
            <div>
              <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-green-200">Distributor Funding</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="dist-funding-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    What is distributor funding and how does it work?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Distributor funding allows you to get instant credit against your pending invoices. Upload your invoices, select the ones you want funded, and receive money directly into your account within hours.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How quickly can I get funded?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Once you submit your invoices for funding, approval typically happens within hours, and funds are disbursed the same day.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="dist-funding-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Do I need collateral for distributor funding?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    No collateral is required. Funding is provided against your invoices and your business performance history.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Retailer Funding FAQs */}
            <div>
              <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-orange-200">Retailer Funding</h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="ret-funding-1" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How can retailers get credit through TurboPay?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Retailers can apply for credit through TurboPay to pay Purchase Invoices of approved distributors. The credit is approved based on your purchase history and relationship with your distributor.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-2" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    What is the credit limit for retailers?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Credit limits are determined based on your purchase history and can go up to ₹1 lakhs. Limits are reviewed regularly and can be increased based on your repayment track record.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-3" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    How do I repay the credit?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Repayment is simple and flexible. You can repay digitally through the TurboPay app or via cash collection by our agents. Payment schedules are designed to align with your business cash flow.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ret-funding-4" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Is there a minimum purchase requirement?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes, the minimum value of Purchase Invoice for which you need funding should be atleast Rupees 5,000.
                  </AccordionContent>
                </AccordionItem>

                {/* <AccordionItem value="ret-funding-5" className="bg-white border border-slate-100 rounded-xl px-6">
                  <AccordionTrigger className="font-outfit text-md font-semibold text-slate-900 hover:no-underline">
                    Can I see a free online demo?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base ">
                    Yes. we will be delighted to give you a demo of TurboPay. You can schedule an online demo here.
                  </AccordionContent>
                </AccordionItem> */}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {/* <a href="/" className="hover:text-[#1F497D] transition-colors">Home</a> */}
              <Link to="/" className="hover:text-[#1F497D] transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="hover:text-[#1F497D] transition-colors">
                Pricing
              </Link>
              <Link to="/faq" className="hover:text-[#1F497D] transition-colors">
                FAQs
              </Link>
              {/* <a href="/faq" className="hover:text-[#1F497D] transition-colors">FAQs</a> */}
              <a href="#" className="hover:text-[#1F497D] transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-400">© 2026 TurboPay. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};